// Self-assessment (focus-area) normalization — the single enforcement point
// for the product rule (JED-1294/JED-1177):
//
//   Self-assessment is INDEPENDENT of every other marking tool. It is included
//   iff the teacher manually selected focus areas. The recommendation engine,
//   the rubrics modal, and other tools' bookkeeping never enable, disable, or
//   clear it.
//
// The invariant: per-question `focusAreaIds` IS the self-assessment state.
// `'self-assessment-areas'` in `feedbackTypes` / `enabledFeedbackTypes` is a
// DERIVED signal, recomputed from the ids here — never trusted as authority
// to clear the ids. (The old save/load transforms did trust it, which is how
// the inline multi-question flow silently wiped focus areas at publish: the
// engine and the rubrics modal stamp explicit feedback-type arrays that never
// contain 'self-assessment-areas', because neither manages it.)
//
// This module is imported by service.js (createAssignment/updateAssignment),
// so it must stay free of component imports.

export const SELF_ASSESSMENT_TYPE = 'self-assessment-areas';

// A "selection" is a non-empty list of real ids. Corrupted legacy shapes —
// [null], [''], duplicate ids — are NOT a selection and must not persist an
// enabled-but-empty self-assessment state.
const sanitizeFocusAreaIds = (rawIds) => [
  ...new Set(
    (Array.isArray(rawIds) ? rawIds : []).filter(
      (id) => typeof id === 'string' && id.trim() !== ''
    )
  ),
];

const syncSelfAssessmentMembership = (types, hasSelection) => {
  if (hasSelection) {
    return types.includes(SELF_ASSESSMENT_TYPE)
      ? types
      : [...types, SELF_ASSESSMENT_TYPE];
  }
  return types.filter((type) => type !== SELF_ASSESSMENT_TYPE);
};

/**
 * Normalize one question's self-assessment fields:
 * - `focusAreaIds` replaced by its sanitized form (unique, non-empty strings);
 * - `focusAreas` filtered to those ids (stale hydrated objects can never
 *   resurrect a selection the teacher removed);
 * - `'self-assessment-areas'` unioned into / removed from whichever of
 *   `feedbackTypes` / `enabledFeedbackTypes` is present AS AN ARRAY. Absent
 *   fields stay absent so each write path keeps its own field shape.
 *
 * Pure: returns a new object, never mutates. Non-object input passes through.
 */
export const normalizeQuestionSelfAssessment = (question) => {
  if (!question || typeof question !== 'object') return question;

  const hasIdsField = Array.isArray(question.focusAreaIds);
  const hasObjectsField = Array.isArray(question.focusAreas);
  const hasFeedbackTypes = Array.isArray(question.feedbackTypes);
  const hasEnabledFeedbackTypes = Array.isArray(question.enabledFeedbackTypes);
  const nestedKeys = ['subQuestions', 'multipartChildren', 'children'].filter(
    (key) => Array.isArray(question[key])
  );

  if (
    !hasIdsField &&
    !hasObjectsField &&
    !hasFeedbackTypes &&
    !hasEnabledFeedbackTypes &&
    nestedKeys.length === 0
  ) {
    return question;
  }

  const ids = sanitizeFocusAreaIds(question.focusAreaIds);
  const hasSelection = ids.length > 0;
  const idSet = new Set(ids);

  const next = { ...question };
  if (hasIdsField) {
    next.focusAreaIds = ids;
  }
  if (hasObjectsField) {
    next.focusAreas = question.focusAreas.filter(
      (fa) => fa != null && idSet.has(fa.id ?? fa._id)
    );
  }
  if (hasFeedbackTypes) {
    next.feedbackTypes = syncSelfAssessmentMembership(
      question.feedbackTypes,
      hasSelection
    );
  }
  if (hasEnabledFeedbackTypes) {
    next.enabledFeedbackTypes = syncSelfAssessmentMembership(
      question.enabledFeedbackTypes,
      hasSelection
    );
  }
  // Multi-part questions can carry nested children under any of the shapes
  // normalizeQuestionsFromApi accepts (subQuestions / multipartChildren /
  // children). Standard write paths flatten before sending, but a raw
  // full-document writer echoing a nested shape must not smuggle
  // un-normalized children past the choke point.
  for (const key of nestedKeys) {
    next[key] = question[key].map(normalizeQuestionSelfAssessment);
  }
  return next;
};

/**
 * Normalize every question of an assignment payload. Applied inside
 * service.js createAssignment/updateAssignment — the choke point ALL writers
 * share (the create/edit flow, task duplication, TaskCard's dashboard
 * publish, and the read-model full-document writers: title edit, stimulus
 * save, comment-bank change).
 *
 * Payloads without a questions array are a real supported shape
 * (transformAssignmentForAPI passes them through unchanged) — return the
 * payload untouched rather than throwing.
 */
export const normalizeAssignmentSelfAssessment = (payload) => {
  if (!payload || !Array.isArray(payload.questions)) return payload;
  return {
    ...payload,
    questions: payload.questions.map(normalizeQuestionSelfAssessment),
  };
};
